import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { DateTime } from 'luxon';
import { get, isEmpty } from 'lodash';
import { cachedInflightRequest } from '@nova/core';
import { cacheableGet } from '@satellite/services/httpClient';

/**
 * options: { fields: string[], joins: string[], includeMetaData: boolean }
 */

/**
 * Days of asset-visit history loaded into the yard waiting queue. Mirrored by the Subspace
 * (websocket) update filtering in the yard composable so live updates and a page refresh show a
 * consistent set of visits. (OD-3730)
 * @type {number}
 */
export const ASSET_VISIT_FETCH_WINDOW_DAYS = 7;

/**
 * Start of the asset-visit fetch window (now minus the configured number of days).
 * @returns {DateTime} Luxon DateTime marking the oldest createDateTime still loaded.
 */
export function getAssetVisitFetchWindowStart() {
  return DateTime.now().minus({ days: ASSET_VISIT_FETCH_WINDOW_DAYS });
}

/**
 * Standard join strings for asset visits in the yard waiting list.
 * @type {string[]}
 */
export const ASSET_VISIT_YARD_JOINS = [
  'company||id,name,scac',
  'appointment||id,start,end,status,statusTimeline,dockId,userId,refNumber,refNumbers,confirmationNumber',
  'appointment.loadType||id,name,direction',
  'appointment.dock||id,name,doorNumber',
  'assetVisitEvents||id,createDateTime,eventType',
  'assetVisitEvents.assetContainer||id,type,code,createDateTime,notes,pickupAppointmentId,dropAppointmentId,state',
  'assetContainers||id,type,code,createDateTime,notes,pickupAppointmentId,dropAppointmentId,state,companyId',
  'assetContainers.company||id,name,scac',
  'assetContainers.assetContainerEvents||id,createDateTime,eventType',
  'spotAssignments||id,spotId,createDateTime'
];

/**
 * Standard join strings for standalone asset containers in the yard waiting list.
 * @type {string[]}
 */
export const ASSET_CONTAINER_YARD_JOINS = [
  'spotAssignments||id,spotId',
  'assetContainerEvents||id,createDateTime,eventType',
  'dropAppointment||id,start,end,status,statusTimeline,dockId,userId,refNumber,refNumbers,confirmationNumber',
  'dropAppointment.dock||id,name,doorNumber',
  'dropAppointment.user||id,firstName,lastName,email',
  'dropAppointment.user.company||id,name',
  'pickupAppointment||id,start,end,status,statusTimeline,dockId,userId,refNumber,refNumbers,confirmationNumber',
  'pickupAppointment.dock||id,name,doorNumber',
  'company||id,name,scac',
  'pickupAppointment.user||id,firstName,lastName,email',
  'pickupAppointment.user.company||id,name'
];

export default {
  async fakeCheckInUnplanned(warehouseId, data, options = {}, requestConfig = {}) {
    const response = await axios.put(
      `checkin/public/${warehouseId}/unplanned`,
      data,
      requestConfig
    );

    return getResponse(response, options);
  },
  async fakeCheckIn(warehouseId, data, options = {}, requestConfig = {}) {
    const response = await axios.put(`checkin/public/${warehouseId}`, data, requestConfig);

    return getResponse(response, options);
  },
  /**
   * Planned asset visit from Helios appointment details after staff has already marked the
   * appointment Arrived (YMS + warehouse check-in form). Uses authenticated CRUD create — not
   * {@link fakeCheckIn}, which calls the public check-in API and only allows Scheduled appointments.
   * @returns {Promise<import('@nova/core').IAssetVisit & { initialMessageFailed?: boolean }>}
   */
  async createPlannedAssetVisitFromLinkedAppointmentModal(body, options = {}, requestConfig = {}) {
    const response = await axios.post('asset-visit', body, {
      ...requestConfig,
      suppressNotification: true
    });

    return getResponse(response, options);
  },
  async getCheckinAppointment(warehouseId, refNumber, options = {}, requestConfig = {}) {
    const response = await axios.get(`checkin/public/${refNumber}/${warehouseId}`, requestConfig);

    return getResponse(response, options);
  },
  async createAssetContainer(data, options = {}, requestConfig = {}) {
    const response = await axios.post('asset-container', data, requestConfig);

    return getResponse(response, options);
  },
  async detachContainer(assetVisitId, assetContainerId, options = {}, requestConfig = {}) {
    const response = await axios.post(
      `asset-visit/${assetVisitId}/detach/${assetContainerId}`,
      null,
      requestConfig
    );

    return getResponse(response, options);
  },
  async attachContainer(assetVisitId, assetContainerId, options = {}, requestConfig = {}) {
    const response = await axios.post(
      `asset-visit/${assetVisitId}/attach/${assetContainerId}`,
      null,
      requestConfig
    );

    return getResponse(response, options);
  },
  async updateAssetContainerStatus(assetContainerId, eventType, options = {}, requestConfig = {}) {
    const response = await axios.post(
      `asset-container/event`,
      { eventType, assetContainerId },
      requestConfig
    );

    return getResponse(response, options);
  },
  async updateAssetVisitStatus(assetVisitId, eventType, options = {}, requestConfig = {}) {
    const response = await axios.post(
      `asset-visit/event`,
      { eventType, assetVisitId },
      requestConfig
    );

    return getResponse(response, options);
  },
  async updateAssetVisit(assetVisitId, data, options = {}, requestConfig = {}) {
    const response = await axios.patch(`asset-visit/${assetVisitId}`, data, requestConfig);

    return getResponse(response, options);
  },
  async updateAssetContainer(assetContainerId, data, options = {}, requestConfig = {}) {
    const response = await axios.patch(`asset-container/${assetContainerId}`, data, requestConfig);

    return getResponse(response, options);
  },
  /**
   * Get only containers that has no asset visit (dettached)
   * @param {*} warehouseId
   * @param {*} params
   * @param {*} options
   * @returns
   */
  async getAssetContainersByWarehouseId(warehouseId, params = {}, options = {}) {
    const baseParams = {
      s: {
        warehouseId,
        assetVisitId: { $is: null },
        isActive: true
      },
      join: ASSET_CONTAINER_YARD_JOINS
    };

    // Generate a unique cache key
    const cacheKey = `asset-container:${warehouseId}:${JSON.stringify(params)}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-container`, { params: { ...baseParams, ...params } });
      return getResponse(response, options);
    });
  },
  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * List asset containers with arbitrary Crud query params (no default warehouse filter).
   * Uses cacheableGet; callers supply `s`, `join`, pagination, etc. via `requestConfig.params`.
   *
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getAssetContainersV2({
    getResponseConfig = {},
    requestConfig = {},
    enableCache = false
  } = {}) {
    const axiosResponse = await cacheableGet('asset-container', {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  /**
   * Get all containers that has or does not have asset visit
   * @param {*} warehouseId
   * @param {*} params
   * @param {*} options
   * @returns
   */
  async getAllAssetContainersByWarehouseId(warehouseId, params = {}, options = {}) {
    const baseParams = {
      s: {
        warehouseId,
        isActive: true
      },
      join: ASSET_CONTAINER_YARD_JOINS
    };

    // Generate a unique cache key
    const cacheKey = `all-asset-containers:${warehouseId}:${JSON.stringify(params)}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-container`, { params: { ...baseParams, ...params } });
      return getResponse(response, options);
    });
  },
  async getLatestAssetVisitsByWarehouseId(warehouseId, params = {}, options = {}) {
    const baseParams = {
      s: {
        isActive: true,
        $and: [
          { warehouseId },
          {
            createDateTime: {
              $gte: getAssetVisitFetchWindowStart().toISO()
            }
          }
        ]
      },
      join: [
        ...ASSET_VISIT_YARD_JOINS,
        'vehicleObservations',
        'vehicleObservations.assetObservations'
      ]
    };

    // Generate a unique cache key
    const cacheKey = `asset-visit:${warehouseId}:${JSON.stringify(params)}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-visit`, { params: { ...baseParams, ...params } });
      return getResponse(response, options);
    });
  },
  /**
   * Get asset visit by appointment id.
   * @param {string} appointmentId - appointment id
   * @param {Object} options
   * @param {Object} options.params - params for the axios call
   * @param {Object} options.getResponseOptions - options for the getResponse function
   * @param {Object} options.requestConfig - request config for the axios call
   * @returns {Promise<Object>}
   */
  async getAssetVisitByAppointmentId(appointmentId, options = {}) {
    const baseParams = {
      s: {
        appointmentId
      }
    };
    const params = { ...baseParams, ...get(options, 'params', {}) };
    const getResponseOptions = { ...get(options, 'getResponseOptions', {}) };
    const requestConfig = { ...get(options, 'requestConfig', {}) };

    // Generate a unique cache key
    const cacheKey = `asset-visit:appointment:${appointmentId}:${JSON.stringify(params)}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-visit`, {
        ...requestConfig,
        params
      });
      const returnArray = getResponse(response, getResponseOptions);

      if (!Array.isArray(returnArray)) {
        return null;
      }

      return get(returnArray, '0') ?? null;
    });
  },
  async getDropAssetContainerByAppointmentId(appointmentId, options = {}) {
    const baseParams = {
      s: {
        dropAppointmentId: appointmentId,
        isActive: true
      }
    };

    const params = { ...baseParams, ...get(options, 'params', {}) };
    const getResponseOptions = { ...get(options, 'getResponseOptions', {}) };
    const requestConfig = { ...get(options, 'requestConfig', {}) };

    // Generate a unique cache key
    const cacheKey = `drop-asset-container:${appointmentId}:${JSON.stringify(params)}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-container`, {
        ...requestConfig,
        params
      });
      return getResponse(response, getResponseOptions);
    });
  },
  /**
   * Asset containers linked to an appointment, by `dropAppointmentId` or `pickupAppointmentId`.
   *
   * `GET asset-container` is a crud `getMany`, i.e. a QueryBuilder — TypeORM honours `eager`
   * relations only on `find*` methods, so nested relations must be requested explicitly through
   * `query.joins` even though `SpotAssignment.spot` and `Spot.spotArea` are eager on the detail
   * route. The search filter goes through `requestConfig.params` because
   * `getQueryStringFromOptions` only emits `fields` and `joins`.
   *
   * @param {'dropAppointmentId' | 'pickupAppointmentId'} searchField
   * @param {string} appointmentId
   * @param {Object} [options]
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query]
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig]
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig]
   * @param {boolean} [options.enableCache] - Inert when `requestConfig.signal` is set.
   * @returns {Promise<unknown>}
   */
  async _getAssetContainersByAppointmentIdV2(
    searchField,
    appointmentId,
    { query = {}, getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const queryString = getQueryStringFromOptions(query);
    const url = `asset-container${isEmpty(queryString) ? '' : `?${queryString}`}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      params: {
        s: { [searchField]: appointmentId, isActive: true },
        ...requestConfig.params
      },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  /**
   * @see _getAssetContainersByAppointmentIdV2
   */
  async getDropAssetContainersByAppointmentIdV2(appointmentId, options = {}) {
    return this._getAssetContainersByAppointmentIdV2('dropAppointmentId', appointmentId, options);
  },
  /**
   * @see _getAssetContainersByAppointmentIdV2
   */
  async getPickupAssetContainersByAppointmentIdV2(appointmentId, options = {}) {
    return this._getAssetContainersByAppointmentIdV2('pickupAppointmentId', appointmentId, options);
  },
  async getPickupAssetContainerByAppointmentId(appointmentId, options = {}) {
    const baseParams = {
      s: {
        pickupAppointmentId: appointmentId,
        isActive: true
      }
    };

    const params = { ...baseParams, ...get(options, 'params', {}) };
    const getResponseOptions = { ...get(options, 'getResponseOptions', {}) };
    const requestConfig = { ...get(options, 'requestConfig', {}) };

    // Generate a unique cache key
    const cacheKey = `pickup-asset-container:${appointmentId}:${JSON.stringify(params)}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-container`, {
        ...requestConfig,
        params
      });
      return getResponse(response, getResponseOptions);
    });
  },
  async getAssetVisitYardEvents(assetVisitId, options = {}) {
    const cacheKey = `asset-visit-yard-events:${assetVisitId}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-visit/${assetVisitId}/yard-events`);
      return getResponse(response, options);
    });
  },
  /**
   * Fetch the first and last "docked" (spot assignment) timestamps for an asset visit.
   *
   * @param {string} assetVisitId - The asset visit id
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (e.g. includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (signal, headers, etc.)
   * @returns {Promise<{ firstDockedAt: string | null, lastDockedAt: string | null }>} ISO timestamps, or `null` when the visit has no spot assignments. When `getResponseConfig.includeMetaData` is set, the payload is wrapped per `getResponse`.
   */
  async getAssetVisitDockedSummary(
    assetVisitId,
    { getResponseConfig = {}, requestConfig = {} } = {}
  ) {
    const response = await axios.get(`asset-visit/${assetVisitId}/docked-summary`, requestConfig);
    return getResponse(response, getResponseConfig);
  },
  async getAssetContainerYardEvents(assetContainerId, options = {}) {
    const cacheKey = `asset-container-yard-events:${assetContainerId}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-container/${assetContainerId}/yard-events`);
      return getResponse(response, options);
    });
  },
  /**
   * Get asset container by ID with spot and area information
   * @param {string} assetContainerId - asset container id
   * @param {Object} options
   * @param {Object} options.params - params for the axios call
   * @param {Object} options.getResponseOptions - options for the getResponse function
   * @param {Object} options.requestConfig - request config for the axios call
   * @returns {Promise<Object>}
   */
  async getAssetContainerById(assetContainerId, options = {}) {
    const baseParams = {
      s: {
        id: assetContainerId,
        isActive: true
      },
      join: ['spotAssignments', 'assetVisit', 'assetVisit.spotAssignments']
    };

    const params = { ...baseParams, ...get(options, 'params', {}) };
    const getResponseOptions = { ...get(options, 'getResponseOptions', {}) };
    const requestConfig = { ...get(options, 'requestConfig', {}) };

    const cacheKey = `asset-container-by-id:${assetContainerId}:${JSON.stringify(params)}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-container/${assetContainerId}`, {
        ...requestConfig,
        params
      });

      return getResponse(response, getResponseOptions);
    });
  },
  async getAssetVisits(params = {}, options = {}) {
    const cacheKey = `asset-visits:${JSON.stringify(params)}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-visit`, { params });
      return getResponse(response, options);
    });
  },
  async getAssetVisitById(assetVisitId, params = {}, options = {}) {
    const cacheKey = `asset-visit-by-id:${assetVisitId}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`asset-visit/${assetVisitId}`, { params });
      return getResponse(response, options);
    });
  },
  /**
   * Asset containers tied to the visit via the Arrived container event (unplanned / no appointment id).
   *
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * @param {string} assetVisitId - Asset visit id
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data (array of asset containers)
   */
  async getAssetVisitArrivalContainersV2(
    assetVisitId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `asset-visit/${assetVisitId}/arrival-container`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  /**
   * Get asset container details with improved API contract
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * @param {string} assetContainerId - asset container id
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getAssetContainerByIdV2(
    assetContainerId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `asset-container/${assetContainerId}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  /**
   * Get asset visit details with improved API contract (minimal data for yard presenters).
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * @param {string} assetVisitId - Asset visit id
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (signal, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getAssetVisitDetails(
    assetVisitId,
    { query = {}, getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const queryString = getQueryStringFromOptions(query);
    const url = `asset-visit/${assetVisitId}${isEmpty(queryString) ? '' : `?${queryString}`}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  async sendReadyForPickupNotification(assetContainerId, emails, options = {}, requestConfig = {}) {
    const response = await axios.post(
      `asset-container/${assetContainerId}/send-notification`,
      { emails, action: 'asset_visit_ready_for_pickup' },
      requestConfig
    );

    return getResponse(response, options);
  },
  /**
   * @param {{ assetVisitId: string, appointmentId: string }} params
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options]
   * @param {import('axios').AxiosRequestConfig} [requestConfig]
   * @returns {Promise<import('@nova/core').IAssetVisit>}
   */
  async unlinkCheckinFromAppointment(
    { assetVisitId, appointmentId },
    options = {},
    requestConfig = {}
  ) {
    const response = await axios.post(
      'asset-visit/unlink-checkin-from-appointment',
      { assetVisitId, appointmentId },
      requestConfig
    );

    return getResponse(response, options);
  }
};
